import { Body1, Body2 } from "@fluentui/react-components";

export const manualLanguages = {
    nl: {
        title: "Handleiding Klokking",
        intro:
            "Welkom bij Klokking, het inschrijfplatform voor kanosprint- en marathon-evenementen. In deze handleiding leggen we stap voor stap uit hoe je een account aanmaakt, je deelnemers beheert en je vereniging inschrijft voor een wedstrijd.",

        sections: [
            {
                number: "1.",
                title: "Wat is Klokking?",
                content: (
                    <Body1>
                        Klokking is een online platform waarmee kanoverenigingen hun leden kunnen aanmelden voor kanosprint- en marathon-evenementen. Via Klokking beheer je je deelnemers, schrijf je in voor evenementen en wijs je deelnemers toe aan boten per afstand, categorie en geslacht.
                    </Body1>
                ),
            },
            {
                number: "2.",
                title: "Account aanmaken",
                content: (
                    <>
                        <Body1>
                            Klokking werkt zonder wachtwoord. Je logt in via een eenmalige code die naar je e-mailadres wordt gestuurd. Volg deze stappen om een account aan te maken:
                        </Body1>
                        <ol>
                            <li>Ga naar de inlogpagina via de knop <strong>Inloggen</strong> in de navigatiebalk.</li>
                            <li>Vul je <strong>e-mailadres</strong> in.</li>
                            <li>Zet het vinkje aan bij <strong>"Nieuw account aanmaken toestaan"</strong>.</li>
                            <li>Klik op <strong>Versturen</strong>. Je ontvangt een eenmalige code per e-mail.</li>
                            <li>Vul de ontvangen <strong>code</strong> in en klik op <strong>Bevestigen</strong>.</li>
                            <li>Je wordt doorgestuurd naar het formulier om je <strong>verenigingsgegevens</strong> in te vullen (zie stap 3).</li>
                        </ol>
                        <Body2>
                            <strong>Let op:</strong> Als je al een account hebt, hoef je het vinkje bij "Nieuw account aanmaken toestaan" <em>niet</em> aan te zetten. Vul alleen je e-mailadres in en bevestig de code.
                        </Body2>
                    </>
                ),
            },
            {
                number: "3.",
                title: "Verenigingsgegevens invullen",
                content: (
                    <>
                        <Body1>
                            Na het aanmaken van een account moet je eenmalig de gegevens van je vereniging invullen. Deze gegevens worden gebruikt voor inschrijvingen en communicatie met de organiserende vereniging.
                        </Body1>
                        <br /><br />
                        <Body2><strong>Vul de volgende gegevens in:</strong></Body2>
                        <ul>
                            <li><strong>Naam vereniging</strong> – de volledige naam van je vereniging.</li>
                            <li><strong>Afkorting</strong> – de gebruikelijke afkorting van je vereniging.</li>
                            <li><strong>Contactpersoon</strong> – naam van de verantwoordelijke voor de inschrijvingen.</li>
                            <li><strong>Telefoonnummer</strong> – telefoonnummer van de contactpersoon.</li>
                            <li><strong>Adres</strong> – adres van de vereniging.</li>
                            <li><strong>E-mailadres</strong> – e-mailadres voor wedstrijdgerelateerde communicatie.</li>
                        </ul>
                        <Body1>
                            Klik op <strong>Opslaan</strong> als je klaar bent. Je kunt deze gegevens later altijd aanpassen via <strong>Instellingen</strong> in de navigatiebalk.
                        </Body1>
                    </>
                ),
            },
            {
                number: "4.",
                title: "Deelnemers beheren",
                content: (
                    <>
                        <Body1>
                            Voordat je je kunt inschrijven voor een wedstrijd, moet je de deelnemers van je vereniging toevoegen. Dit doe je via de pagina <strong>Deelnemers</strong> in de navigatiebalk.
                        </Body1>
                        <br /><br />
                        <Body2><strong>Een deelnemer toevoegen:</strong></Body2>
                        <ol>
                            <li>Ga naar <strong>Deelnemers</strong> via de navigatiebalk.</li>
                            <li>Klik op <strong>Deelnemer toevoegen</strong>.</li>
                            <li>Vul de volgende gegevens in:
                                <ul>
                                    <li><strong>Voornaam</strong> en <strong>achternaam</strong> (verplicht)</li>
                                    <li><strong>Geboortejaar</strong> (optioneel)</li>
                                    <li><strong>WSV-licentienummer</strong> (optioneel, voor verificatie door de bond)</li>
                                </ul>
                            </li>
                            <li>Klik op <strong>Opslaan</strong>.</li>
                        </ol>
                        <Body2><strong>Een deelnemer bewerken of verwijderen:</strong></Body2>
                        <ul>
                            <li>Klik op een deelnemer in de lijst om de gegevens te bewerken of de deelnemer te verwijderen.</li>
                        </ul>
                        <Body2>
                            <strong>Let op:</strong> Deelnemers die je verwijdert, worden ook verwijderd uit alle toekomstige inschrijvingen.
                        </Body2>
                    </>
                ),
            },
            {
                number: "5.",
                title: "Inschrijven voor een wedstrijd",
                content: (
                    <>
                        <Body1>
                            Als je deelnemers zijn toegevoegd, kun je je inschrijven voor een wedstrijd. Volg deze stappen:
                        </Body1>
                        <ol>
                            <li>Ga naar de <strong>Startpagina</strong>. Hier zie je een overzicht van beschikbare wedstrijden.</li>
                            <li>Klik op een wedstrijd om de details te bekijken (naam, locatie, datum, contactgegevens).</li>
                            <li>Klik op <strong>Inschrijven</strong> om naar de inschrijfpagina te gaan.</li>
                            <li>Op de inschrijfpagina zie je de beschikbare afstanden, ingedeeld per <strong>geslacht</strong>, <strong>categorie</strong> en <strong>boottype</strong>.</li>
                            <li>Klik op de knop <strong>Deelnemer toevoegen</strong> naast de gewenste afstand.</li>
                            <li>Selecteer de deelnemer(s) die je wilt inschrijven:
                                <ul>
                                    <li>Voor een <strong>1 persoonsboot</strong>: selecteer 1 deelnemer.</li>
                                    <li>Voor een <strong>2 persoonsboot</strong>: selecteer tot 2 deelnemers.</li>
                                    <li>Voor een <strong>4 persoonsboot</strong>: selecteer tot 4 deelnemers.</li>
                                </ul>
                            </li>
                            <li>Klik op <strong>Inschrijven</strong>. De deelnemer(s) worden toegevoegd aan een nieuwe boot voor die afstand.</li>
                        </ol>
                        <Body2>
                            <strong>Tip:</strong> Je kunt deelnemers ook toevoegen aan een bestaande boot (voor meerpersoons boten). Ga daarvoor op <strong>Toevoegen aan bestaande boot</strong> en voer de gewenste boot-ID in.
                            Deelnemers van andere verenigingen/accounts kunnen ook worden toegevoegd aan een boot. Hiervoor moet je de boot-ID delen met de andere vereniging/account. Vanwege privacyredenen zie je alleen deelnemers van je eigen vereniging.
                        </Body2>
                    </>
                ),
            },
            {
                number: "6.",
                title: "Inschrijving bekijken en verwijderen",
                content: (
                    <>
                        <Body1>
                            Op de inschrijfpagina zie je een overzicht van alle inschrijvingen van je vereniging voor de betreffende wedstrijd.
                        </Body1>
                        <br /><br />
                        <Body2><strong>Inschrijving verwijderen:</strong></Body2>
                        <ul>
                            <li>Zoek de inschrijving op in het overzicht.</li>
                            <li>Klik op het <strong>verwijderpictogram</strong> naast de inschrijving.</li>
                            <li>Bevestig de verwijdering. De inschrijving wordt definitief verwijderd.</li>
                        </ul>
                        <Body2>
                            <strong>Let op:</strong> Verwijderde inschrijvingen kunnen niet worden hersteld.
                        </Body2>
                    </>
                ),
            },
            {
                number: "7.",
                title: "Wedstrijdschema bekijken",
                content: (
                    <Body1>
                        Via de knop <strong>Schema</strong> op de evenementpagina kun je het wedstrijdschema en de uitslagen bekijken. Dit overzicht is ook beschikbaar zonder account.
                    </Body1>
                ),
            },
            {
                number: "8.",
                title: "Accountbeheer",
                content: (
                    <>
                        <Body1>
                            Via <strong>Instellingen</strong> in de navigatiebalk kun je de gegevens van je vereniging aanpassen of je account verwijderen.
                        </Body1>
                        <br /><br />
                        <Body2><strong>Verenigingsgegevens aanpassen:</strong></Body2>
                        <ul>
                            <li>Pas de gewenste velden aan en klik op <strong>Opslaan</strong>.</li>
                        </ul>
                        <Body2><strong>Account verwijderen:</strong></Body2>
                        <ul>
                            <li>Scroll naar beneden op de instellingenpagina en klik op <strong>Account verwijderen</strong>.</li>
                            <li>Bevestig de verwijdering. Je account en alle bijbehorende gegevens worden definitief verwijderd.</li>
                        </ul>
                        <Body2>
                            <strong>Let op:</strong> Het verwijderen van je account kan niet ongedaan worden gemaakt. Neem contact op via <span className="privacy-email">klokking@pm.me</span> als je vragen hebt.
                        </Body2>
                    </>
                ),
            },
        ],

        updated: "Handleiding bijgewerkt: 2026",
    },

    en: {
        title: "Klokking User Manual",
        intro:
            "Welcome to Klokking, the registration platform for canoe sprint and marathon events. This manual explains step by step how to create an account, manage your participants, and register your club for a competition.",

        sections: [
            {
                number: "1.",
                title: "What is Klokking?",
                content: (
                    <Body1>
                        Klokking is an online platform that allows canoe clubs to register their members for canoe sprint and marathon events. Through Klokking you can manage your participants, register for events, and assign participants to boats per distance, category, and gender.
                    </Body1>
                ),
            },
            {
                number: "2.",
                title: "Creating an account",
                content: (
                    <>
                        <Body1>
                            Klokking uses passwordless login. You sign in using a one-time code sent to your email address. Follow these steps to create an account:
                        </Body1>
                        <ol>
                            <li>Go to the login page via the <strong>Login</strong> button in the navigation bar.</li>
                            <li>Enter your <strong>email address</strong>.</li>
                            <li>Check the box labeled <strong>"Allow creating new account"</strong>.</li>
                            <li>Click <strong>Send</strong>. You will receive a one-time code by email.</li>
                            <li>Enter the received <strong>code</strong> and click <strong>Confirm</strong>.</li>
                            <li>You will be redirected to a form to fill in your <strong>club details</strong> (see step 3).</li>
                        </ol>
                        <Body2>
                            <strong>Note:</strong> If you already have an account, you do <em>not</em> need to check "Allow creating new account". Just enter your email address and confirm the code.
                        </Body2>
                    </>
                ),
            },
            {
                number: "3.",
                title: "Entering club information",
                content: (
                    <>
                        <Body1>
                            After creating an account, you need to fill in your club's details once. These details are used for registrations and communication with the organizing club.
                        </Body1>
                        <br /><br />
                        <Body2><strong>Fill in the following details:</strong></Body2>
                        <ul>
                            <li><strong>Club name</strong> – the full name of your club.</li>
                            <li><strong>Abbreviation</strong> – the common abbreviation of your club.</li>
                            <li><strong>Contact person</strong> – name of the person responsible for registrations.</li>
                            <li><strong>Phone number</strong> – phone number of the contact person.</li>
                            <li><strong>Address</strong> – address of the club.</li>
                            <li><strong>Email address</strong> – email address for competition-related communication.</li>
                        </ul>
                        <Body1>
                            Click <strong>Save</strong> when done. You can update these details at any time via <strong>Settings</strong> in the navigation bar.
                        </Body1>
                    </>
                ),
            },
            {
                number: "4.",
                title: "Managing participants",
                content: (
                    <>
                        <Body1>
                            Before registering for a competition, you need to add your club's participants. You do this via the <strong>Participants</strong> page in the navigation bar.
                        </Body1>
                        <br /><br />
                        <Body2><strong>Adding a participant:</strong></Body2>
                        <ol>
                            <li>Go to <strong>Participants</strong> in the navigation bar.</li>
                            <li>Click <strong>Add participant</strong>.</li>
                            <li>Fill in the following details:
                                <ul>
                                    <li><strong>First name</strong> and <strong>last name</strong> (required)</li>
                                    <li><strong>Birth year</strong> (optional)</li>
                                    <li><strong>WSV license number</strong> (optional, for federation verification)</li>
                                </ul>
                            </li>
                            <li>Click <strong>Save</strong>.</li>
                        </ol>
                        <Body2><strong>Editing or deleting a participant:</strong></Body2>
                        <ul>
                            <li>Click on a participant in the list to edit their details or delete them.</li>
                        </ul>
                        <Body2>
                            <strong>Note:</strong> Deleted participants will also be removed from all future registrations.
                        </Body2>
                    </>
                ),
            },
            {
                number: "5.",
                title: "Registering for a competition",
                content: (
                    <>
                        <Body1>
                            Once your participants are added, you can register for a competition. Follow these steps:
                        </Body1>
                        <ol>
                            <li>Go to the <strong>Home page</strong>. Here you will see an overview of available competitions.</li>
                            <li>Click on a competition to view its details (name, location, date, contact info).</li>
                            <li>Click <strong>Register</strong> to go to the registration page.</li>
                            <li>On the registration page you will see the available distances, organized by <strong>gender</strong>, <strong>category</strong>, and <strong>boat type</strong>.</li>
                            <li>Click the <strong>Add participant</strong> button next to the desired distance.</li>
                            <li>Select the participant(s) you want to register:
                                <ul>
                                    <li>For a <strong>1-person boat</strong>: select 1 participant.</li>
                                    <li>For a <strong>2-person boat</strong>: select up to 2 participants.</li>
                                    <li>For a <strong>4-person boat</strong>: select up to 4 participants.</li>
                                </ul>
                            </li>
                            <li>Click <strong>Register</strong>. The participant(s) will be added to a new boat for that distance.</li>
                        </ol>
                        <Body2>
                            <strong>Tip:</strong> You can also add participants to an existing boat. Go to <strong>Toevoegen aan bestaande boot</strong> and enter the desired boat ID.
                            Participants of other clubs/accounts can also be added to a boat. You need to share the boat ID with the other club/account for this. For privacy reasons, you will only see participants from your own club.
                        </Body2>
                    </>
                ),
            },
            {
                number: "6.",
                title: "Viewing and deleting registrations",
                content: (
                    <>
                        <Body1>
                            On the registration page you can see an overview of all your club's registrations for the competition.
                        </Body1>
                        <br /><br />
                        <Body2><strong>Deleting a registration:</strong></Body2>
                        <ul>
                            <li>Find the registration in the overview.</li>
                            <li>Click the <strong>delete icon</strong> next to the registration.</li>
                            <li>Confirm the deletion. The registration will be permanently removed.</li>
                        </ul>
                        <Body2>
                            <strong>Note:</strong> Deleted registrations cannot be restored.
                        </Body2>
                    </>
                ),
            },
            {
                number: "7.",
                title: "Viewing the competition schedule",
                content: (
                    <Body1>
                        Via the <strong>Schedule</strong> button on the event page you can view the competition schedule and results. This overview is also available without an account.
                    </Body1>
                ),
            },
            {
                number: "8.",
                title: "Account management",
                content: (
                    <>
                        <Body1>
                            Via <strong>Settings</strong> in the navigation bar you can update your club details or delete your account.
                        </Body1>
                        <br /><br />
                        <Body2><strong>Updating club details:</strong></Body2>
                        <ul>
                            <li>Update the desired fields and click <strong>Save</strong>.</li>
                        </ul>
                        <Body2><strong>Deleting your account:</strong></Body2>
                        <ul>
                            <li>Scroll down on the settings page and click <strong>Delete account</strong>.</li>
                            <li>Confirm the deletion. Your account and all associated data will be permanently deleted.</li>
                        </ul>
                        <Body2>
                            <strong>Note:</strong> Deleting your account cannot be undone. Contact us at <span className="privacy-email">klokking@pm.me</span> if you have any questions.
                        </Body2>
                    </>
                ),
            },
        ],

        updated: "Manual last updated: 2026",
    },
};


export const boatIdLanguages = {
    nl: "Met een boot-ID koppel je deelnemers aan een boot. Wil je een nieuwe boot aanmaken? Schrijf eerst 1 persoon in (de ID wordt automatisch gegenereerd), en koppel de andere deelnemers later met de boot-ID. Deelnemers van andere verenigingen/accounts kunnen ook worden toegevoegd aan een boot. Hiervoor moet je de boot-ID delen met de andere vereniging/account. Vanwege privacyredenen zie je alleen deelnemers van je eigen vereniging.",
    en: "With a boat ID you can link participants to a boat. Do you want to create a new boat? First register 1 person (the ID is automatically generated), and connect the other participants later with the boat ID. Participants of other clubs/accounts can also be added to a boat. You need to share the boat ID with the other club/account for this. For privacy reasons, you will only see participants from your own club.\n"
}